import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import customerMachineryApi from "../../../customer/machinery/api/customerMachineryApi";
import edgeMonitorApi from "../edgeMonitorApi";
import {
  MachineConnectionDto,
  SiteAgentDto,
} from "../models/edgeMonitorModels";
import useEdgeMonitorSignalR from "../hooks/useEdgeMonitorSignalR";
import InternalToolsSidebar from "./InternalToolsSidebar";
import Zone1Header from "./Zone1Header";
import Zone2SignalGrid from "./Zone2SignalGrid";
import Zone3EventStream from "./Zone3EventStream";
import "./edgeLiveMonitor.scss";

const FONT_SORA = "'Sora', system-ui, -apple-system, sans-serif";

const PAGE_BG = "#FAFAF7";

/**
 * GUID validator — matches the standard 8-4-4-4-12 hex form. Used to
 * short-circuit obvious-malformed query params before hitting the API.
 */
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const EdgeLiveMonitorPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const machineIdRaw = searchParams.get("machineId");
  const machineId = machineIdRaw && GUID_RE.test(machineIdRaw) ? machineIdRaw : null;
  const machineIdInvalid = Boolean(machineIdRaw) && !machineId;

  const [machineName, setMachineName] = useState<string | null>(null);
  const [machineNotFound, setMachineNotFound] = useState(false);
  const [siteAgents, setSiteAgents] = useState<SiteAgentDto[] | null>(null);
  const [siteAgentNoAccess, setSiteAgentNoAccess] = useState(false);
  const [connection, setConnection] = useState<MachineConnectionDto | null>(null);

  // Inject Sora + JetBrains Mono + Inter once on mount. Independent of
  // useRedesignSetup() because this page lives outside the customer redesign
  // tree and uses its own off-white ground per spec §5.1.
  useEffect(() => {
    const linkId = "edge-monitor-fonts";
    if (document.getElementById(linkId)) return;
    const link = document.createElement("link");
    link.id = linkId;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&family=Inter:wght@400;500;600&display=swap";
    document.head.appendChild(link);
  }, []);

  // Real fix v4 (2026-05-07) — paint the dark-left-band gradient on <html>
  // AND <body> while this route is mounted. The global rule in index.scss
  // (`html, body, #root, #root>div { height: 100% }`) clamps body to
  // exactly 100vh; when the page content (signal grid + Zone 4) overflows
  // and the user scrolls, the page-root gradient (which lives INSIDE body)
  // can't paint past where body ends, so the default <html> background
  // shows through. Debug instrumentation in commit c5925f61 confirmed it
  // was dodgerblue (html bg) leaking. Adding the class to html + body
  // ensures the gradient extends to the document root. Cleanup on unmount
  // prevents bleed-over to other routes. See edgeLiveMonitor.scss.
  useEffect(() => {
    const cls = "edge-live-monitor-active";
    document.documentElement.classList.add(cls);
    document.body.classList.add(cls);
    return () => {
      document.documentElement.classList.remove(cls);
      document.body.classList.remove(cls);
    };
  }, []);

  // Fetch machine name (cross-service via Machinery API).
  useEffect(() => {
    setMachineName(null);
    setMachineNotFound(false);
    if (!machineId) return;
    let cancelled = false;
    customerMachineryApi
      .getMachine(machineId)
      .then((m) => {
        if (cancelled) return;
        if (m == null) {
          setMachineNotFound(true);
          return;
        }
        const name =
          [m.manufacturer?.name, m.model].filter(Boolean).join(" ") || m.model || "—";
        setMachineName(name);
      })
      .catch(() => {
        if (cancelled) return;
        setMachineNotFound(true);
      });
    return () => {
      cancelled = true;
    };
  }, [machineId]);

  // Fetch site-agents list (Admin-gated → 403 collapses to null).
  useEffect(() => {
    let cancelled = false;
    const fetchAgents = () => {
      edgeMonitorApi.getSiteAgents().then((agents) => {
        if (cancelled) return;
        if (agents == null) {
          setSiteAgentNoAccess(true);
          setSiteAgents(null);
          return;
        }
        setSiteAgentNoAccess(false);
        setSiteAgents(agents);
      });
    };
    fetchAgents();
    // poll while mounted; backend has no SiteAgentUpdated SignalR event yet
    const intervalId = window.setInterval(fetchAgents, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  // Fetch machine-connection list to find the one for this machine. Endpoint
  // is admin-gated server-side (same scoping as site-agents); on 403 the
  // S7-link panel renders blank gracefully.
  useEffect(() => {
    if (!machineId) {
      setConnection(null);
      return;
    }
    // We don't have a direct getConnectionByMachineId endpoint exposed yet —
    // for the install operator at Save-tech Milan, the page intentionally
    // does NOT depend on listing all customer connections. Phase B falls back
    // to status.agentTimestamp + status.updatedAt for "last data Ns ago"
    // rendering and leaves connection null in v1.
    setConnection(null);
  }, [machineId]);

  // Live data via SignalR + REST polling fallback.
  const {
    status,
    events,
    connState,
    pollingRatePerMin,
    errorsLast5Min,
    lastPushAt,
    lastDataAt,
    notFoundOrNoAccess: machineStatusNotFound,
  } = useEdgeMonitorSignalR(machineId);

  // Derive a SiteAgent for the header. Without a getConnectionByMachineId
  // endpoint we can't pick the exact agent for this machine — the install
  // scenario at Save-tech Milan typically has exactly ONE agent in the list,
  // so we use the first active one as a reasonable default. When §13 / future
  // work adds a per-machine connection lookup, narrow this.
  const siteAgent = useMemo<SiteAgentDto | null>(() => {
    if (!siteAgents || siteAgents.length === 0) return null;
    return siteAgents.find((a) => a.isActive) ?? siteAgents[0];
  }, [siteAgents]);

  // Bulletproof full-height left rail: paint the dark sidebar colour as a
  // left-edge band on the page-root background using a hard-stop gradient.
  // The aside renders ON TOP at exactly the same colour and width, so visually
  // they're indistinguishable — but the parent's painted band guarantees the
  // dark column extends from the very top of the page-root all the way down
  // to wherever the dark Zone 4 footer begins (Zone 4 is the last child of
  // the main column, so its top is at the page-root's bottom edge). No
  // dependence on flex `align-self: stretch` (which proved unreliable on
  // 2026-05-07 when the parent has `min-height: 100vh` rather than `height`).
  const SIDEBAR_DARK = "#1A1F1A";
  const SIDEBAR_WIDTH = 240;
  const PAGE_BG_WITH_RAIL = `linear-gradient(to right, ${SIDEBAR_DARK} 0, ${SIDEBAR_DARK} ${SIDEBAR_WIDTH}px, ${PAGE_BG} ${SIDEBAR_WIDTH}px, ${PAGE_BG} 100%)`;

  if (machineIdInvalid) {
    return (
      <div style={{ display: "flex", minHeight: "100vh", background: PAGE_BG_WITH_RAIL }}>
        <InternalToolsSidebar />
        <div style={{ flex: 1, padding: "60px 40px", fontFamily: FONT_SORA, color: "#1A1A1A" }}>
          <h2 style={{ fontSize: "20px", fontWeight: 600 }}>Invalid machineId</h2>
          <p style={{ color: "#6B6B6B", fontSize: "13px" }}>
            The provided <code>machineId</code> is not a valid GUID.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        background: PAGE_BG_WITH_RAIL,
        fontFamily: FONT_SORA,
        color: "#1A1A1A",
      }}
    >
      <InternalToolsSidebar />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {/* Zone 1 — header. NOT sticky in empty state because the header IS
            the hero; sticky only when a machine is loaded. Polling rate +
            errors-last-5m fields previously lived in Zone 4 footer; the
            footer was removed 2026-05-08 and these fields moved up here. */}
        <div style={machineId ? { position: "sticky", top: 0, zIndex: 5 } : undefined}>
          <Zone1Header
            machineId={machineId}
            machineName={machineName}
            machineNotFoundOrNoAccess={machineNotFound || machineStatusNotFound}
            siteAgent={siteAgent}
            siteAgentNoAccess={siteAgentNoAccess}
            connection={connection}
            status={status}
            connState={connState}
            lastPushAt={lastPushAt}
            pollingRatePerMin={pollingRatePerMin}
            errorsLast5Min={errorsLast5Min}
            errorWindowStartAt={
              machineId ? new Date(Date.now() - 5 * 60_000).toISOString() : null
            }
            pollingEventsInWindow={pollingRatePerMin}
          />
        </div>

        {/* Zones 2 + 3 — main split, only when a machine is loaded.
            CRITICAL layout rules (do not regress):
              1. `flex: "1 1 auto"` (== `flex: auto`) — grow to fill any
                 remaining vertical space in the inner-column flex stack.
                 Originally needed so the (now-removed) Zone 4 footer
                 landed at page-root bottom; still needed today so that
                 short content (small status snapshot) does not leave a
                 cream gap below Zones 2/3 — the inner column needs
                 these zones to stretch the column's full height.
              2. `flex-basis: auto` (NOT `0%`!) — the prior bug from
                 commit b420c0ea (40-tile grid overflows past viewport)
                 happened with `flex: 1` (basis 0%): the row's hypothetical
                 main-size collapses to 0 and content draws outside.
                 `flex: 1 1 auto` keeps basis at content-size, then grows
                 from there if there's extra room. Tall content (40-tile
                 grid) is fully accommodated; short content gets the row
                 stretched to fill remaining space.
              3. NO `minHeight: 0` — keeps the row from shrinking below
                 its content (default `min-height: auto` does this for us
                 when basis is auto).
              4. The dark left rail extends to the page bottom via the
                 html+body gradient class `edge-live-monitor-active` in
                 edgeLiveMonitor.scss. With Zone 4 removed (2026-05-08),
                 the rail now reaches the very bottom of the page rather
                 than the footer top. */}
        {machineId && !(machineNotFound && !status) && (
          <div style={{ display: "flex", flex: "1 1 auto" }}>
            <div style={{ flex: 1, padding: "16px 0 24px 32px", minWidth: 0 }}>
              <Zone2SignalGrid status={status} lastDataAt={lastDataAt} />
            </div>
            <Zone3EventStream events={events} />
          </div>
        )}

        {/* Empty-state placeholders for Zones 2/3 — dashed outlines at
            ~0.55 opacity per artboard 4SG-0. Reused when no machineId is
            loaded so the user sees the page shape with the picker as the
            focal element above. (Zone 4 placeholder removed 2026-05-08
            along with the live diagnostics footer.) */}
        {!machineId && (
          <div
            style={{
              background: PAGE_BG,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              opacity: 0.55,
              padding: "32px",
            }}
          >
            <div style={{ display: "flex", flex: 1, gap: "14px" }}>
              <div
                style={{
                  alignItems: "center",
                  background: "#FFFFFF",
                  borderColor: "#D8D2C5",
                  borderRadius: "10px",
                  borderStyle: "dashed",
                  borderWidth: "1px",
                  display: "flex",
                  flex: 1,
                  flexDirection: "column",
                  gap: "10px",
                  justifyContent: "center",
                  padding: "60px",
                }}
              >
                <div
                  style={{
                    color: "#B0AA9D",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "10px",
                    fontWeight: 500,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                  }}
                >
                  Zone 2 · PLC signal grid
                </div>
                <div
                  style={{
                    color: "#8A857B",
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "13px",
                    lineHeight: "18px",
                    maxWidth: "280px",
                    textAlign: "center",
                  }}
                >
                  Awaiting machine selection. 39 polled signals across 7 categories will render here.
                </div>
              </div>
              <div
                style={{
                  alignItems: "center",
                  background: "#FFFFFF",
                  borderColor: "#D8D2C5",
                  borderRadius: "10px",
                  borderStyle: "dashed",
                  borderWidth: "1px",
                  display: "flex",
                  flexDirection: "column",
                  flexShrink: 0,
                  gap: "10px",
                  justifyContent: "center",
                  padding: "60px 24px",
                  width: "438px",
                }}
              >
                <div
                  style={{
                    color: "#B0AA9D",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "10px",
                    fontWeight: 500,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                  }}
                >
                  Zone 3 · Event stream
                </div>
                <div
                  style={{
                    color: "#8A857B",
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "13px",
                    lineHeight: "18px",
                    maxWidth: "240px",
                    textAlign: "center",
                  }}
                >
                  Last 50 events will stream here once a machine is loaded.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EdgeLiveMonitorPage;
